import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { MapPin, Video, Plus, QrCode, Printer, Download, Power, Pencil } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import {
  Card, Button, Input, Field, Badge, Alert, Spinner, EmptyState, Modal, Table,
} from '../components/ui';

/**
 * Locations, and the printable QR each physical one carries.
 *
 * The code encodes /checkin/<token> on this origin. Online locations get no
 * code: there is no entrance to scan at, and the server refuses check-in for
 * them anyway.
 *
 * Tokens are not readable from the locations table — admin fetches one through
 * location_checkin_token() only when rendering a code to print.
 */

const CheckInPoster = ({ location, onClose }) => {
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const build = async () => {
      const { data: token, error: tokenError } = await supabase.rpc('location_checkin_token', {
        p_location_id: location.id,
      });
      if (tokenError) { setError(tokenError.message); return; }
      const url = `${window.location.origin}/checkin/${token}`;
      // Generous error correction: these get printed and live on a wall.
      const png = await QRCode.toDataURL(url, { width: 720, margin: 2, errorCorrectionLevel: 'H' });
      setDataUrl(png);
    };
    build();
  }, [location.id]);

  const print = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>${location.name}</title>
      <style>
        body { font-family: system-ui, sans-serif; text-align: center; padding: 40px; }
        h1 { font-size: 34px; margin: 0 0 4px; }
        p  { color: #475569; margin: 0 0 28px; font-size: 18px; }
        img { width: 460px; height: 460px; }
        .hint { margin-top: 28px; font-size: 20px; color: #1e293b; }
      </style></head>
      <body onload="window.print()">
        <h1>${location.name}</h1>
        <p>${location.address ?? ''}</p>
        <img src="${dataUrl}" alt="QR" />
        <p class="hint">${t('locations.posterHint')}</p>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <Modal open title={`${t('locations.qrCode')} · ${location.name}`} onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
          {dataUrl && (
            <>
              <a href={dataUrl} download={`qr-${location.name.replace(/\s+/g, '-').toLowerCase()}.png`}>
                <Button variant="secondary"><Download size={16} />{t('locations.downloadQr')}</Button>
              </a>
              <Button onClick={print}><Printer size={16} />{t('locations.printQr')}</Button>
            </>
          )}
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : !dataUrl ? <Spinner /> : (
        <div className="text-center">
          <img src={dataUrl} alt="QR" className="mx-auto w-64" />
          <p className="mt-3 text-sm text-slate-600">{t('locations.qrHelp')}</p>
        </div>
      )}
    </Modal>
  );
};

const LocationsView = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [showing, setShowing] = useState(null);
  // null = closed, 'new' = creating, otherwise the location being edited.
  // A sede's address reaches patients through the QR poster and the calendar
  // invitation, so there has to be a way to correct it after it is created.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: '', address: '', is_online: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Explicit columns, not '*': checkin_token is revoked from `authenticated`
    // so tokens cannot be enumerated, and selecting it fails the whole query.
    // Admin reads a token through location_checkin_token() when printing.
    const { data, error } = await supabase
      .from('locations')
      .select('id,name,address,is_online,timezone,is_active')
      .order('name');
    if (error) setNotice({ tone: 'danger', text: error.message });
    setLocations(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setDraft({ name: '', address: '', is_online: false });
    setEditing('new');
  };

  const openEdit = (location) => {
    setDraft({
      name: location.name,
      address: location.address ?? '',
      is_online: location.is_online,
    });
    setEditing(location);
  };

  const save = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);

    const values = {
      name: draft.name.trim(),
      // an online location carries no street address, per the schema constraint
      address: draft.is_online ? null : (draft.address.trim() || null),
      is_online: draft.is_online,
    };

    const { error } = editing === 'new'
      ? await supabase.from('locations').insert([values])
      : await supabase.from('locations').update(values).eq('id', editing.id);

    setBusy(false);
    if (error) { setNotice({ tone: 'danger', text: error.message }); return; }
    setNotice({ tone: 'success', text: t('locations.saved') });
    setEditing(null);
    setDraft({ name: '', address: '', is_online: false });
    load();
  };

  const toggleActive = async (location) => {
    await supabase.from('locations').update({ is_active: !location.is_active }).eq('id', location.id);
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('locations.title')}</h1>
          <p className="text-sm text-slate-500">{t('locations.subtitle')}</p>
        </div>
        <Button onClick={openNew}><Plus size={16} />{t('locations.addLocation')}</Button>
      </div>

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

      <Card>
        {locations.length === 0 ? (
          <EmptyState icon={MapPin} title={t('locations.none')} />
        ) : (
          <Table headers={[t('locations.location'), t('locations.address'), t('common.status'), '']}>
            {locations.map((location) => (
              <tr key={location.id}>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    {location.is_online
                      ? <Video size={16} className="text-slate-400" />
                      : <MapPin size={16} className="text-slate-400" />}
                    <span className="font-medium text-slate-900">{location.name}</span>
                    {location.is_online && <Badge tone="info">{t('locations.online')}</Badge>}
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-slate-600">
                  {location.is_online ? <span className="text-slate-400">—</span> : (location.address || '—')}
                </td>
                <td className="px-3 py-3">
                  <Badge tone={location.is_active ? 'success' : 'neutral'}>
                    {location.is_active ? t('locations.active') : t('masterData.inactive')}
                  </Badge>
                </td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-2">
                    {/* No QR for online: nothing to scan at a video call. */}
                    {!location.is_online && (
                      <Button variant="secondary" onClick={() => setShowing(location)}>
                        <QrCode size={14} />{t('locations.qrCode')}
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => openEdit(location)}
                            title={t('common.edit')}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" onClick={() => toggleActive(location)}>
                      <Power size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {showing && <CheckInPoster location={showing} onClose={() => setShowing(null)} />}

      <Modal
        open={editing !== null}
        title={editing === 'new' ? t('locations.addLocation') : t('locations.editLocation')}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={save} loading={busy}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('locations.location')} required>
            <Input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.is_online}
              onChange={(event) => setDraft({ ...draft, is_online: event.target.checked })}
            />
            {t('locations.isOnline')}
          </label>
          {!draft.is_online && (
            <Field label={t('locations.address')} hint={t('locations.addressHint')}>
              <Input value={draft.address} onChange={(event) => setDraft({ ...draft, address: event.target.value })} />
            </Field>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LocationsView;
